import { useState } from "react";
import SectionCard from "./SectionCard";

type Props = {
  onChange: (msg: string) => void;
};

type Role = "Owner" | "Admin" | "Dispatcher" | "Read-only";

type TeamMember = {
  id: string;
  name: string;
  email: string;
  avatar: string;
  role: Role;
  status: "active" | "invited";
};

const roleHint: Record<Role, string> = {
  Owner: "Full access · billing · ownership",
  Admin: "Bookings, cleaners, customers, payouts",
  Dispatcher: "Schedule and reassign bookings",
  "Read-only": "View reports, no changes",
};

const initialTeam: TeamMember[] = [
  {
    id: "you",
    name: "Eleanor Hayes",
    email: "eleanor@purenest.co",
    avatar:
      "https://readdy.ai/api/search-image?query=Warm%20friendly%20portrait%20of%20a%20woman%20in%20her%20thirties%20wearing%20cream%20sweater%20natural%20soft%20studio%20light%20editorial%20photography%20approachable%20clean%20background&width=120&height=120&seq=admin-team-eleanor&orientation=squarish",
    role: "Owner",
    status: "active",
  },
  {
    id: "leo",
    name: "Leo Marchetti",
    email: "leo@purenest.co",
    avatar:
      "https://readdy.ai/api/search-image?query=Warm%20portrait%20of%20bearded%20man%20in%20his%20thirties%20gentle%20smile%20olive%20shirt%20natural%20soft%20studio%20light%20editorial%20photography%20approachable%20clean%20background&width=120&height=120&seq=admin-team-leo&orientation=squarish",
    role: "Admin",
    status: "active",
  },
  {
    id: "nina",
    name: "Nina Park",
    email: "nina@purenest.co",
    avatar:
      "https://readdy.ai/api/search-image?query=Warm%20portrait%20of%20young%20woman%20with%20short%20hair%20gentle%20smile%20sage%20sweater%20natural%20soft%20studio%20light%20editorial%20photography%20approachable%20clean%20background&width=120&height=120&seq=admin-team-nina&orientation=squarish",
    role: "Dispatcher",
    status: "active",
  },
  {
    id: "sam",
    name: "Sam Diallo",
    email: "sam.d@purenest.co",
    avatar:
      "https://readdy.ai/api/search-image?query=Warm%20portrait%20of%20young%20person%20gentle%20smile%20cream%20henley%20natural%20soft%20studio%20light%20editorial%20photography%20approachable%20clean%20background&width=120&height=120&seq=admin-team-sam&orientation=squarish",
    role: "Read-only",
    status: "invited",
  },
];

const allRoles: Role[] = ["Owner", "Admin", "Dispatcher", "Read-only"];

const rolePill: Record<Role, string> = {
  Owner: "bg-primary-100 text-primary-700",
  Admin: "bg-accent-100 text-accent-700",
  Dispatcher: "bg-secondary-100 text-secondary-800",
  "Read-only": "bg-background-200 text-foreground-700",
};

export default function TeamSection({ onChange }: Props) {
  const [team, setTeam] = useState<TeamMember[]>(initialTeam);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [draft, setDraft] = useState<{ email: string; role: Role }>({
    email: "",
    role: "Admin",
  });

  const changeRole = (id: string, role: Role) => {
    const member = team.find((m) => m.id === id);
    setTeam((arr) => arr.map((m) => (m.id === id ? { ...m, role } : m)));
    setOpenMenu(null);
    if (member) onChange(`${member.name.split(" ")[0]} set to ${role}`);
  };

  const removeMember = (id: string) => {
    const member = team.find((m) => m.id === id);
    setTeam((arr) => arr.filter((m) => m.id !== id));
    setOpenMenu(null);
    if (member) onChange(`${member.name} removed from team`);
  };

  const sendInvite = () => {
    if (!draft.email.trim()) return;
    const id = "invite-" + Math.random().toString(36).slice(2, 6);
    setTeam((arr) => [
      ...arr,
      {
        id,
        name: draft.email.split("@")[0],
        email: draft.email.trim(),
        avatar:
          "https://readdy.ai/api/search-image?query=Warm%20friendly%20neutral%20portrait%20gentle%20smile%20cream%20background%20editorial%20natural%20soft%20studio%20light%20approachable%20person&width=120&height=120&seq=admin-team-invited&orientation=squarish",
        role: draft.role,
        status: "invited",
      },
    ]);
    onChange(`Invite sent to ${draft.email.trim()}`);
    setDraft({ email: "", role: "Admin" });
    setInviting(false);
  };

  return (
    <SectionCard
      title="Team & permissions"
      description="Add teammates who can manage PureNest with you."
    >
      <ul className="divide-y divide-background-200">
        {team.map((m) => (
          <li key={m.id} className="flex items-center gap-3 py-3 first:pt-0">
            <img
              src={m.avatar}
              alt={m.name}
              className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-foreground-950 truncate">
                  {m.name}
                </p>
                {m.status === "invited" && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-100 text-accent-700">
                    Invited
                  </span>
                )}
              </div>
              <p className="text-[11px] text-foreground-600 truncate">{m.email}</p>
            </div>
            <span
              className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${rolePill[m.role]}`}
            >
              {m.role}
            </span>
            <div className="relative">
              <button
                onClick={() => setOpenMenu(openMenu === m.id ? null : m.id)}
                disabled={m.role === "Owner"}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
                aria-label="Member actions"
              >
                <i className="ri-more-2-fill"></i>
              </button>
              {openMenu === m.id && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setOpenMenu(null)}
                  ></div>
                  <div className="absolute right-0 top-9 z-40 w-56 bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                    <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-foreground-500 border-b border-background-200">
                      Change role
                    </div>
                    {allRoles
                      .filter((r) => r !== "Owner")
                      .map((r) => (
                        <button
                          key={r}
                          onClick={() => changeRole(m.id, r)}
                          className="w-full flex items-start justify-between gap-3 px-3 py-2 text-xs text-foreground-800 hover:bg-background-100 text-left cursor-pointer"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{r}</p>
                            <p className="text-[10px] text-foreground-600">
                              {roleHint[r]}
                            </p>
                          </div>
                          {m.role === r && (
                            <i className="ri-check-line text-primary-600 mt-0.5"></i>
                          )}
                        </button>
                      ))}
                    <button
                      onClick={() => removeMember(m.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-accent-700 hover:bg-background-100 border-t border-background-200 cursor-pointer"
                    >
                      <i className="ri-user-unfollow-line"></i>
                      Remove from team
                    </button>
                  </div>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>

      {inviting ? (
        <div className="mt-4 p-4 rounded-lg bg-background-100">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-2">
            <input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              placeholder="teammate@email.com"
              className="bg-background-50 text-sm text-foreground-900 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-primary-300"
              autoFocus
            />
            <select
              value={draft.role}
              onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value as Role }))}
              className="bg-background-50 text-sm text-foreground-900 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              {allRoles
                .filter((r) => r !== "Owner")
                .map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
            </select>
          </div>
          <p className="text-[10px] text-foreground-600 mt-2">{roleHint[draft.role]}</p>
          <div className="flex items-center justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setInviting(false);
                setDraft({ email: "", role: "Admin" });
              }}
              className="px-3 py-1.5 rounded-md text-xs text-foreground-700 hover:bg-background-200 cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={sendInvite}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-send-plane-line"></i>
              Send invite
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setInviting(true)}
          className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md border border-dashed border-background-300 text-foreground-700 hover:bg-background-100 text-xs font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-mail-add-line"></i>
          Invite a teammate
        </button>
      )}
    </SectionCard>
  );
}